// Normaliza la respuesta del endpoint /api/public/directory (proxy en
// /api/directory) y el fallback estático teamMembersStatic.js al mismo formato
// plano que consume TeamSection.
//
// El backend doc: un mismo profesional puede aparecer dentro del array staff[]
// de varios centros; debemos consolidar por staff.id juntando las ubicaciones.

import teamMembersStatic from './teamMembersStatic';

// El orden curado del equipo (Miguel Ángel CEO primero, etc.) es la lista
// hardcoded de teamMembersStatic. Lo usamos como fuente de verdad porque el
// backend devuelve el staff por orden de inserción de centers, que es
// arbitrario. Staff nuevo que no esté en la lista cae al final por nombre.
const CURATED_NAMES = teamMembersStatic.map((m) => m.name);

function curatedOrderIndex(name) {
  const idx = CURATED_NAMES.indexOf(name);
  return idx === -1 ? Infinity : idx;
}

function sortStaffByCuratedOrder(staffList) {
  return [...staffList].sort((a, b) => {
    const ai = curatedOrderIndex(a.name);
    const bi = curatedOrderIndex(b.name);
    if (ai !== bi) return ai - bi;
    return a.name.localeCompare(b.name, 'es');
  });
}

function sortLocationsByLabel(locations) {
  // Orden estable alfabético es-ES. El backend devuelve los centros por orden
  // de inserción en el pivot (≈ arbitrario), así que ordenamos en cliente para
  // que la lista por staff sea predecible.
  return [...locations].sort((a, b) => a.label.localeCompare(b.label, 'es'));
}

function buildStaffEntry(s, locations) {
  return {
    id: s.id,
    name: s.name,
    jobTitle: s.jobTitle ?? null,
    bio: s.bio ?? null,
    credentials: s.credentials && s.credentials.length > 0 ? s.credentials : null,
    photoUrl: s.photoUrl ?? null,
    functions: s.functions && s.functions.length > 0 ? s.functions : null,
    websiteUrl: s.websiteUrl ?? null,
    locations,
  };
}

export function adaptStaff(apiResponse) {
  const centers = apiResponse?.centers ?? [];
  const byId = new Map();

  // Staff asignado a centro(s): acumula locations consolidando duplicados por staff.id.
  for (const center of centers) {
    const staffArray = center.staff ?? [];
    for (const s of staffArray) {
      if (!s || !s.id) continue;

      const location = {
        centerName: center.name,
        city: center.city ?? null,
        province: center.province ?? null,
        label: center.city || center.name,
      };

      const existing = byId.get(s.id);
      if (existing) {
        existing.locations.push(location);
      } else {
        byId.set(s.id, buildStaffEntry(s, [location]));
      }
    }
  }

  // Staff transversal (sin centro asignado) — backend lo expone en globalStaff[].
  for (const s of apiResponse?.globalStaff ?? []) {
    if (!s || !s.id || byId.has(s.id)) continue;
    byId.set(s.id, buildStaffEntry(s, []));
  }

  return sortStaffByCuratedOrder(
    Array.from(byId.values()).map((entry) => ({
      ...entry,
      locations: sortLocationsByLabel(entry.locations),
    }))
  );
}

function splitLocationText(text) {
  if (!text) return [];
  return text
    .split(/,|\s+[yY]\s+/)
    .map((s) => s.trim())
    .filter(Boolean);
}

export function flattenStatic(staticMembers) {
  return staticMembers.map((m, index) => {
    const credentials = m.description
      ? m.description.split(',').map((c) => c.trim()).filter(Boolean)
      : null;
    const functions = m.tags && m.tags.length > 0 ? m.tags : null;
    const locations = sortLocationsByLabel(
      splitLocationText(m.location).map((label) => ({
        centerName: label,
        city: null,
        province: null,
        label,
      }))
    );
    return {
      id: `static-${index}`,
      name: m.name,
      jobTitle: m.position ?? null,
      bio: null,
      credentials: credentials && credentials.length > 0 ? credentials : null,
      photoUrl: m.image ?? null,
      functions,
      websiteUrl: m.link ?? null,
      locations,
    };
  });
}
